/**
 * P1.10 — Admin cohort viewer + grant/revoke for BETA segments.
 *
 * Wraps the backend endpoints from
 * `backend/src/routes/admin-user-segments.ts`:
 *   - GET  /v1/admin/segments/:segment/users   (cohort list, paginated)
 *   - POST /v1/admin/users/:userId/segments    (grant)
 *   - DELETE /v1/admin/users/:userId/segments/:segment  (soft-revoke)
 *
 * UX:
 *   - Segment selector at the top (4 fixed cohorts).
 *   - Grant button opens an inline form (user_id + source + notes).
 *   - Cohort table below with email / display_name / source / granted_at /
 *     granted_by + a Revoke action per row that prompts for a reason.
 *
 * Memory invariants honored:
 *   - useCustom URLs are RELATIVE — the dataProvider already prepends
 *     apiUrl. Adding apiUrl here would produce double-host URLs (per
 *     `feedback_admin_useCustom_double_apiurl_2026_05_08`).
 */
import { useState, FormEvent } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { toast } from '@/hooks/useToast';
import { formatDate } from '@/lib/utils';
import { SortableHeader, nextSort, type SortOrder } from '@/components/table/SortableHeader';
import {
  Users as UsersIcon,
  UserPlus,
  Upload,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Trash2,
} from 'lucide-react';

const SEGMENTS = [
  { value: 'friends_family', label: 'Friends & Family' },
  { value: 'founding_100', label: 'Founding 100' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'beta_general', label: 'General BETA' },
] as const;

const SOURCES = [
  { value: 'admin', label: 'Admin (manual grant)' },
  { value: 'waitlist', label: 'Waitlist promotion' },
  { value: 'referral', label: 'Referral' },
  { value: 'manual', label: 'Manual (off-platform)' },
] as const;

type SegmentValue = (typeof SEGMENTS)[number]['value'];
type SourceValue = (typeof SOURCES)[number]['value'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PAGE_SIZE = 25;

interface CohortRow {
  user_id: string;
  email: string | null;
  display_name: string | null;
  granted_at: string;
  granted_by: string | null;
  source: SourceValue;
}

interface CohortResponse {
  segment: SegmentValue;
  total: number;
  limit: number;
  offset: number;
  users: CohortRow[];
}

export function UserSegmentsPage() {
  const [segment, setSegment] = useState<SegmentValue>('founding_100');
  const [offset, setOffset] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);

  const handleSort = (field: string) => {
    const next = nextSort({ field: sortField, order: sortOrder }, field);
    setSortField(next.field);
    setSortOrder(next.order);
    setOffset(0);
  };

  const { data, isLoading, isFetching, refetch } = useCustom<CohortResponse>({
    url: `/v1/admin/segments/${segment}/users`,
    method: 'get',
    config: {
      query: {
        limit: PAGE_SIZE,
        offset,
        ...(sourceFilter ? { source: sourceFilter } : {}),
        ...(search ? { search } : {}),
        ...(sortField ? { sort_by: sortField, sort_order: sortOrder } : {}),
      },
    },
  });

  const cohort = data?.data;
  const users = cohort?.users ?? [];
  const total = cohort?.total ?? 0;
  const hasNextPage = offset + PAGE_SIZE < total;
  const hasPrevPage = offset > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UsersIcon className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold">User Segments</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setShowGrantForm((v) => !v);
              if (!showGrantForm) setShowBulkForm(false);
            }}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {showGrantForm ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            {showGrantForm ? 'Cancel grant' : 'Grant cohort'}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowBulkForm((v) => !v);
              if (!showBulkForm) setShowGrantForm(false);
            }}
            className="flex items-center gap-2 rounded-md border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            {showBulkForm ? <X className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
            {showBulkForm ? 'Cancel bulk' : 'Bulk grant (CSV)'}
          </button>
        </div>
      </div>

      {showGrantForm && (
        <GrantForm
          defaultSegment={segment}
          onClose={() => setShowGrantForm(false)}
          onGranted={() => {
            setShowGrantForm(false);
            void refetch();
          }}
        />
      )}

      {showBulkForm && (
        <BulkGrantForm
          defaultSegment={segment}
          onClose={() => setShowBulkForm(false)}
          onGranted={() => {
            void refetch();
          }}
        />
      )}

      {/* Segment selector */}
      <div className="flex flex-wrap gap-2 rounded-md border bg-card p-3">
        {SEGMENTS.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => {
              setSegment(s.value);
              setOffset(0);
            }}
            className={
              s.value === segment
                ? 'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground'
                : 'rounded-md border bg-background px-4 py-2 text-sm font-medium hover:bg-accent'
            }
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Source + email filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value);
            setOffset(0);
          }}
          aria-label="Filter by source"
          className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">All sources</option>
          {SOURCES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
            setOffset(0);
          }}
          className="flex items-center gap-2"
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search email…"
            className="h-9 w-56 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            type="submit"
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Search
          </button>
        </form>
      </div>

      {/* Cohort table */}
      <div className="rounded-md border bg-card">
        <div className="border-b px-4 py-3 text-sm text-muted-foreground">
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading cohort…
            </span>
          ) : (
            <>
              <span className="font-medium text-foreground">{total}</span>{' '}
              users in <span className="font-medium text-foreground">
                {SEGMENTS.find((s) => s.value === segment)?.label}
              </span>
            </>
          )}
        </div>

        {!isLoading && users.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            No users in this cohort yet. Use the &quot;Grant cohort&quot; button to add one.
          </div>
        )}

        {users.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <SortableHeader field="email" label="Email" activeField={sortField} activeOrder={sortOrder} onSort={handleSort} />
                  <th className="px-4 py-3 font-medium">Display name</th>
                  <SortableHeader field="source" label="Source" activeField={sortField} activeOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader field="granted_at" label="Granted" activeField={sortField} activeOrder={sortOrder} onSort={handleSort} />
                  <th className="px-4 py-3 font-medium">User ID</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <CohortRowItem
                    key={u.user_id}
                    row={u}
                    segment={segment}
                    onRevoked={() => void refetch()}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(hasPrevPage || hasNextPage) && (
          <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
            <span className="text-muted-foreground">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!hasPrevPage || isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="flex items-center gap-1 rounded-md border px-3 py-1.5 hover:bg-accent disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>
              <button
                type="button"
                disabled={!hasNextPage || isFetching}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="flex items-center gap-1 rounded-md border px-3 py-1.5 hover:bg-accent disabled:opacity-50"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Row + revoke ──────────────────────────────────────────────────────────

function CohortRowItem({
  row,
  segment,
  onRevoked,
}: {
  row: CohortRow;
  segment: SegmentValue;
  onRevoked: () => void;
}) {
  const { mutate, isLoading } = useCustomMutation();

  const handleRevoke = () => {
    const reason = window.prompt(
      `Revoke ${SEGMENTS.find((s) => s.value === segment)?.label} for ${row.email ?? row.user_id}?\n\nReason (required, ≥3 chars):`,
    );
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast({ title: 'Revocation reason must be at least 3 characters.', variant: 'destructive' });
      return;
    }

    mutate(
      {
        url: `/v1/admin/users/${row.user_id}/segments/${segment}`,
        method: 'delete',
        values: { reason: reason.trim() },
      },
      {
        onSuccess: () => {
          toast({
            title: `Revoked ${segment}`,
            description: `for ${row.email ?? row.user_id}`,
          });
          onRevoked();
        },
        onError: (err) => {
          toast({
            title: 'Revoke failed',
            description: err?.message ?? 'unknown error',
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="px-4 py-3">{row.email ?? <span className="text-muted-foreground">—</span>}</td>
      <td className="px-4 py-3">
        {row.display_name ?? <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-4 py-3">
        <span className="rounded bg-secondary px-2 py-0.5 text-xs font-medium">{row.source}</span>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{formatDate(row.granted_at)}</td>
      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{row.user_id.slice(0, 8)}…</td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          disabled={isLoading}
          onClick={handleRevoke}
          className="inline-flex items-center gap-1 rounded-md border border-destructive/40 px-3 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          Revoke
        </button>
      </td>
    </tr>
  );
}

// ─── Grant form ────────────────────────────────────────────────────────────

function GrantForm({
  defaultSegment,
  onClose,
  onGranted,
}: {
  defaultSegment: SegmentValue;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [userId, setUserId] = useState('');
  const [segment, setSegment] = useState<SegmentValue>(defaultSegment);
  const [source, setSource] = useState<SourceValue>('admin');
  const [notes, setNotes] = useState('');

  const { mutate, isLoading } = useCustomMutation();

  const userIdValid = UUID_RE.test(userId.trim());
  const canSubmit = userIdValid && !isLoading;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    mutate(
      {
        url: `/v1/admin/users/${userId.trim()}/segments`,
        method: 'post',
        values: {
          segment,
          source,
          notes: notes.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          toast({
            title: `Granted ${segment}`,
            description: `to user ${userId.slice(0, 8)}…`,
          });
          setUserId('');
          setNotes('');
          onGranted();
        },
        onError: (err) => {
          // ConflictError (409) means active grant already exists. Surface
          // the backend message verbatim so the admin sees "already has a
          // grant" instead of a generic "error".
          toast({
            title: 'Grant failed',
            description: err?.message ?? 'unknown error',
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border bg-card p-4 space-y-3"
    >
      <h2 className="text-sm font-medium">Grant cohort segment</h2>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">User ID (UUID)</span>
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className={
              'block w-full rounded-md border px-3 py-2 text-sm font-mono ' +
              (userId && !userIdValid ? 'border-destructive' : 'border-input')
            }
          />
          {userId && !userIdValid && (
            <span className="text-xs text-destructive">Must be a valid UUID</span>
          )}
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Segment</span>
          <select
            value={segment}
            onChange={(e) => setSegment(e.target.value as SegmentValue)}
            className="block w-full rounded-md border border-input px-3 py-2 text-sm"
          >
            {SEGMENTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Source</span>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as SourceValue)}
            className="block w-full rounded-md border border-input px-3 py-2 text-sm"
          >
            {SOURCES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1 sm:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">Notes (optional)</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Founding 100 launch list, batch 1"
            maxLength={500}
            className="block w-full rounded-md border border-input px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          Grant
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Bulk grant form ───────────────────────────────────────────────────────

type BulkGrantRowStatus = 'granted' | 'skipped_existing' | 'not_found' | 'invalid';

interface BulkGrantRowResult {
  identifier: string;
  user_id: string | null;
  email: string | null;
  status: BulkGrantRowStatus;
  message?: string;
}

interface BulkGrantResponse {
  segment: SegmentValue;
  source: SourceValue;
  total: number;
  granted: number;
  skipped_existing: number;
  not_found: number;
  invalid: number;
  rows: BulkGrantRowResult[];
}

function parseIdentifiers(raw: string): string[] {
  // Split on newlines, commas, semicolons, tabs. Strip whitespace per entry.
  // Drop empties. The backend de-duplicates, but doing it here makes the
  // count-preview honest before submit.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const tok of raw.split(/[\s,;]+/)) {
    const t = tok.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

function BulkGrantForm({
  defaultSegment,
  onClose,
  onGranted,
}: {
  defaultSegment: SegmentValue;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [csvText, setCsvText] = useState('');
  const [segment, setSegment] = useState<SegmentValue>(defaultSegment);
  const [source, setSource] = useState<SourceValue>('admin');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<BulkGrantResponse | null>(null);

  const { mutate, isLoading } = useCustomMutation<BulkGrantResponse>();

  const identifiers = parseIdentifiers(csvText);
  const canSubmit = identifiers.length > 0 && identifiers.length <= 500 && !isLoading;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    mutate(
      {
        url: `/v1/admin/segments/${segment}/bulk-grant`,
        method: 'post',
        values: {
          identifiers,
          source,
          notes: notes.trim() || undefined,
        },
      },
      {
        onSuccess: (data) => {
          const summary = data.data;
          setResult(summary);
          toast({
            title: `Bulk grant complete`,
            description: `${summary.granted} granted, ${summary.skipped_existing} already had it, ${summary.not_found} not found, ${summary.invalid} invalid.`,
          });
          if (summary.granted > 0) onGranted();
        },
        onError: (err) => {
          toast({
            title: 'Bulk grant failed',
            description: err?.message ?? 'unknown error',
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleFile = async (file: File) => {
    const text = await file.text();
    setCsvText(text);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border bg-card p-4 space-y-3"
    >
      <h2 className="text-sm font-medium">
        Bulk grant segment (paste CSV or upload .csv / .txt)
      </h2>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Segment</span>
          <select
            value={segment}
            onChange={(e) => setSegment(e.target.value as SegmentValue)}
            className="block w-full rounded-md border border-input px-3 py-2 text-sm"
          >
            {SEGMENTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Source</span>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as SourceValue)}
            className="block w-full rounded-md border border-input px-3 py-2 text-sm"
          >
            {SOURCES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Notes (optional)</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Batch tag, e.g. Founding 100 launch"
            maxLength={500}
            className="block w-full rounded-md border border-input px-3 py-2 text-sm"
          />
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium text-muted-foreground">
          Emails or user UUIDs, one per line (or comma/semicolon/tab-separated).
          Max 500 per call.
        </span>
        <textarea
          rows={6}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder={
            'alice@example.com\nbob@example.com\n11111111-2222-4333-8444-555555555555'
          }
          className="block w-full rounded-md border border-input px-3 py-2 text-sm font-mono"
        />
      </label>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <input
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
          className="text-xs"
        />
        <span>
          {identifiers.length === 0
            ? 'Paste or upload to preview'
            : identifiers.length > 500
              ? `${identifiers.length} parsed — exceeds 500 limit`
              : `${identifiers.length} unique identifier${identifiers.length === 1 ? '' : 's'} ready`}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {isLoading ? 'Granting…' : `Grant ${identifiers.length} now`}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          Close
        </button>
      </div>

      {result && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-3 rounded-md border bg-muted/30 px-3 py-2 text-xs">
            <Pill label="granted" count={result.granted} tone="success" />
            <Pill label="already had" count={result.skipped_existing} tone="neutral" />
            <Pill label="not found" count={result.not_found} tone="warn" />
            <Pill label="invalid" count={result.invalid} tone="error" />
          </div>
          <div className="max-h-60 overflow-y-auto rounded-md border">
            <table className="w-full text-xs">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Identifier</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={`${r.identifier}-${i}`} className="border-b last:border-0">
                    <td className="px-3 py-1.5 font-mono">{r.identifier}</td>
                    <td className="px-3 py-1.5">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground">
                      {r.message ?? r.user_id?.slice(0, 8) ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </form>
  );
}

function Pill({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'success' | 'neutral' | 'warn' | 'error';
}) {
  const toneClass = {
    success: 'bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-100',
    neutral: 'bg-muted text-foreground',
    warn: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
    error: 'bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-100',
  }[tone];
  return (
    <span className={`rounded px-2 py-0.5 font-medium ${toneClass}`}>
      {count} {label}
    </span>
  );
}

function StatusPill({ status }: { status: BulkGrantRowStatus }) {
  const tone =
    status === 'granted'
      ? 'success'
      : status === 'skipped_existing'
        ? 'neutral'
        : status === 'not_found'
          ? 'warn'
          : 'error';
  return <Pill label={status} count={1} tone={tone} />;
}
